// src/pages/Contacts/ContactsIndex.tsx
/**
 * Timber Management System — ContactsIndex (SavedViews + URL sync + debounce)
 *
 * Features
 * • Tabs: Contacts / Contact Users / Deleted
 * • Filters: Search, Types (multi), Tags (multi), State, Has Email
 * • URL-synced filters: q, t (types), g (tags), st (state), e (has email), tab, dens
 * • Saved Views via <SavedViews storageKey="opts:contactViews:v1" .../>
 * • Debounced search (250ms)
 * • Bulk action: Copy Emails (unique, comma-separated) for filtered set
 * • DataTable with CSV export & column chooser (storageKey="tbl:contacts:v1")
 * • Drawer for create/edit with soft delete/restore
 * • LocalStorage migration from vendors:data:v1 → contacts:data:v1
 * • Audit via logAudit({action,details}, role); notifications via useNotifications().push(...)
 *
 * Assumptions from app brief
 * • MultiSelect accepts string[] options (and persists via storageKey).
 * • Notifications provider exposes useNotifications().push({ title, body?, level? }).
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AppShell from '@/components/AppShell'
import DataTable, { Col } from '@/components/DataTable'
import Drawer from '@/components/Drawer'
import Skeleton from '@/components/Skeleton'
import EmptyState from '@/components/EmptyState'
import MultiSelect from '@/components/MultiSelect'
import SavedViews from '@/components/SavedViews'
import { useNotifications } from '@/lib/notifications'
import { get, set } from '@/lib/storage'
import { logAudit } from '@/lib/audit'
import {
  ListFilter,
  Save,
  Mail,
  Copy,
  Trash2,
  Undo2,
  Edit,
  Info as InfoIcon,
} from 'lucide-react'

type Role = 'Admin'|'Dispatcher'|'Loader'|'Trucker'|'Accountant'
const role: Role = get<Role>('role', 'Admin' as Role)

type Contact = {
  id: string
  fullName: string
  company?: string
  emails?: string[]
  phone?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  types: string[]
  tags: string[]
  deleted?: boolean
  createdAt?: string
  updatedAt?: string
}

type TabKey = 'contacts' | 'users' | 'deleted'
type Filters = {
  q: string
  types: string[]
  tags: string[]
  state: string
  hasEmail: boolean
  density: 'comfort'|'compact'
  tab: TabKey
}

const DATA_KEY = 'contacts:data:v1'
const LEGACY_DATA_KEY = 'vendors:data:v1'
const TYPES_KEY = 'opts:contactTypes:v1'
const TAGS_KEY  = 'opts:contactTags:v1'
const VIEWS_KEY = 'opts:contactViews:v1'

const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '—')
const uniq = <T,>(arr: T[]) => Array.from(new Set(arr))

export default function ContactsIndex() {
  const { push } = useNotifications()
  const [params, setParams] = useSearchParams()

  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)

  // Filters init from URL
  const [filters, setFilters] = useState<Filters>(() => {
    const dens = (params.get('dens') as 'comfort'|'compact' | null) || (get<'comfort'|'compact'>('ui:table:density','compact') || 'compact')
    const tab  = (params.get('tab') as TabKey) || 'contacts'
    return {
      q: params.get('q') || '',
      types: (params.get('t') || '').split(',').filter(Boolean),
      tags:  (params.get('g') || '').split(',').filter(Boolean),
      state: params.get('st') || '',
      hasEmail: params.get('e') === '1',
      density: dens,
      tab,
    }
  })

  // Debounced search
  const [qRaw, setQRaw] = useState(filters.q)
  const [debouncedQ, setDebouncedQ] = useState(filters.q)
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(qRaw), 250); return () => clearTimeout(t) }, [qRaw])
  useEffect(() => { if (debouncedQ !== filters.q) setFilters(f => ({ ...f, q: debouncedQ })) }, [debouncedQ])

  const [editing, setEditing] = useState<Contact | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Load + migrate data
  useEffect(() => {
    setLoading(true)
    const data = get<Contact[]>(DATA_KEY, [])
    if (data && data.length) {
      setContacts(normalizeContacts(data))
      setLoading(false)
      return
    }
    const legacy = get<any[]>(LEGACY_DATA_KEY, [])
    if (legacy && legacy.length) {
      const migrated = normalizeContacts(legacy.map(v => ({
        id: v.id || crypto.randomUUID(),
        fullName: v.name || v.fullName || '',
        company: v.company || v.companyName || '',
        emails: parseEmails(v.emails || v.email),
        phone: v.phone || '',
        address1: v.address1 || '', address2: v.address2 || '',
        city: v.city || '', state: v.state || '', zip: v.zip || '',
        types: Array.isArray(v.types) ? v.types : [],
        tags: Array.isArray(v.tags) ? v.tags : [],
        createdAt: v.createdAt, updatedAt: v.updatedAt, deleted: !!v.deleted,
      })))
      setContacts(migrated); set(DATA_KEY, migrated)
    }
    setLoading(false)
  }, [])

  // Persist density + sync filters → URL
  useEffect(() => {
    set('ui:table:density', filters.density)
    try {
      if (filters.q) params.set('q', filters.q); else params.delete('q')
      params.set('tab', filters.tab)
      if (filters.types.length) params.set('t', filters.types.join(',')); else params.delete('t')
      if (filters.tags.length)  params.set('g', filters.tags.join(',')); else params.delete('g')
      if (filters.state) params.set('st', filters.state); else params.delete('st')
      if (filters.hasEmail) params.set('e','1'); else params.delete('e')
      params.set('dens', filters.density)
      setParams(params, { replace: true })
    } catch {}
  }, [filters, params, setParams])

  const typeOptions = get<string[]>(TYPES_KEY, [])
  const tagOptions  = get<string[]>(TAGS_KEY, [])

  // Filtered list
  const filtered = useMemo(() => {
    const term = debouncedQ.trim().toLowerCase()
    return contacts.filter(c => {
      if (filters.tab === 'deleted' && !c.deleted) return false
      if (filters.tab !== 'deleted' && c.deleted) return false
      if (filters.hasEmail && !(c.emails && c.emails.length)) return false
      if (filters.types.length) {
        const has = filters.types.some(t => c.types?.includes(t))
        if (!has) return false
      }
      if (filters.tags.length) {
        const has = filters.tags.some(t => c.tags?.includes(t))
        if (!has) return false
      }
      if (filters.state && c.state?.toLowerCase() !== filters.state.toLowerCase()) return false
      if (term) {
        const hay = [
          c.fullName, c.company, c.emails?.join(' '), c.phone,
          c.city, c.state, c.tags?.join(' '), c.types?.join(' ')
        ].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(term)) return false
      }
      return true
    })
  }, [contacts, filters, debouncedQ])

  // Users tab: group by email
  const userGroups = useMemo(() => {
    const map = new Map<string, Contact[]>()
    for (const c of filtered) {
      for (const e of (c.emails || [])) {
        const k = e.trim().toLowerCase()
        if (!k) continue
        if (!map.has(k)) map.set(k, [])
        map.get(k)!.push(c)
      }
    }
    return Array.from(map.entries()).map(([email, list]) => ({
      email,
      count: list.length,
      companies: uniq(list.map(x => x.company || '').filter(Boolean))
    }))
  }, [filtered])

  // Table columns
  const cols: Col[] = [
    { key: 'name', label: 'Name' },
    { key: 'company', label: 'Company' },
    { key: 'types', label: 'Types' },
    { key: 'emails', label: 'Emails' },
    { key: 'phone', label: 'Phone' },
    { key: 'citystate', label: 'City, State' },
    { key: 'tags', label: 'Tags' },
    { key: 'actions', label: 'Actions' },
  ]

  const rows = filtered.map(c => ({
    name: (
      <div className="pl-3 pr-2 min-w-[16rem]">
        <div className="font-medium truncate" title={c.fullName}>{c.fullName}</div>
        <div className="text-xs text-slate-500 mt-0.5">Updated {fmtDate(c.updatedAt)}</div>
      </div>
    ),
    company: <div className="min-w-[12rem] truncate" title={c.company}>{c.company || '—'}</div>,
    types: (
      <div className="flex flex-wrap gap-1 min-w-[10rem]">
        {(c.types || []).map(t => <span key={t} className="badge">{t}</span>)}
      </div>
    ),
    emails: <div className="min-w-[14rem] truncate" title={(c.emails||[]).join(', ')}>{(c.emails || []).join(', ') || '—'}</div>,
    phone:  <div className="min-w-[8rem]">{c.phone || '—'}</div>,
    citystate: <div className="min-w-[10rem]">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</div>,
    tags: (
      <div className="flex flex-wrap gap-1 min-w-[10rem]">
        {(c.tags || []).map(t => <span key={t} className="badge">{t}</span>)}
      </div>
    ),
    actions: (
      <div className="flex justify-end gap-2 pr-2 min-w-[10rem]">
        <button className="btn-secondary px-2 py-1" title="Edit" onClick={() => openEdit(c)}>
          <Edit className="w-4 h-4" />
        </button>
        <button className="btn-secondary px-2 py-1" title="Copy Emails" onClick={() => copyEmails([c])}>
          <Mail className="w-4 h-4" />
        </button>
        {(!c.deleted && role === 'Admin') && (
          <button className="btn-danger px-2 py-1" title="Delete (soft)" onClick={() => softDelete(c)}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        {(c.deleted && role === 'Admin') && (
          <button className="btn-secondary px-2 py-1" title="Restore" onClick={() => restore(c)}>
            <Undo2 className="w-4 h-4" />
          </button>
        )}
      </div>
    ),
  }))

  const densityClass = filters.density === 'compact' ? 'density-compact' : ''

  // Actions
  function openEdit(c?: Contact) {
    const base: Contact = c ? { ...c } : {
      id: crypto.randomUUID(),
      fullName: '',
      company: '',
      emails: [], phone: '',
      address1: '', address2: '', city: '', state: '', zip: '',
      types: [], tags: [], deleted: false,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    }
    setEditing(base); setDrawerOpen(true)
  }
  function persist(next: Contact[]) { set(DATA_KEY, next); setContacts(next) }
  function saveEditing() {
    if (!editing) return
    const next = upsertById(contacts, { ...editing, updatedAt: new Date().toISOString() })
    persist(next)
    push({ title: 'Saved contact', body: editing.fullName })
    logAudit({ action: 'contacts:save', details: { id: editing.id } }, role)
    setDrawerOpen(false)
  }
  function softDelete(c: Contact) {
    const next = upsertById(contacts, { ...c, deleted: true, updatedAt: new Date().toISOString() })
    persist(next)
    push({ title: 'Deleted (soft)', body: c.fullName, level: 'warning' })
    logAudit({ action: 'contacts:delete', details: { id: c.id } }, role)
  }
  function restore(c: Contact) {
    const next = upsertById(contacts, { ...c, deleted: false, updatedAt: new Date().toISOString() })
    persist(next)
    push({ title: 'Restored', body: c.fullName })
    logAudit({ action: 'contacts:restore', details: { id: c.id } }, role)
  }
  function copyEmails(items: Contact[] = filtered) {
    const emails = uniq(items.flatMap(c => c.emails || [])).filter(Boolean)
    if (emails.length === 0) {
      push({ title: 'No emails found', level: 'warning' })
      return
    }
    const text = emails.join(', ')
    navigator.clipboard?.writeText(text).then(() => {
      push({ title: 'Emails copied', body: `${emails.length} addresses` })
    }).catch(() => {
      const ta = document.createElement('textarea')
      ta.value = text; document.body.appendChild(ta); ta.select()
      try { document.execCommand('copy') } catch {}
      ta.remove()
      push({ title: 'Emails copied', body: `${emails.length} addresses` })
    })
    logAudit({ action: 'contacts:emails:copy', details: { count: emails.length } }, role)
  }

  // Users tab table
  const userCols: Col[] = [
    { key: 'email', label: 'Email' },
    { key: 'count', label: 'Contacts' },
    { key: 'companies', label: 'Companies' },
    { key: 'actions', label: 'Actions' },
  ]
  const userRows = userGroups.map(u => ({
    email: <div className="pl-3 pr-2 min-w-[16rem]">{u.email}</div>,
    count: <div className="text-right tabular-nums min-w-[6rem]">{u.count.toLocaleString()}</div>,
    companies: <div className="min-w-[16rem] truncate" title={u.companies.join(', ')}>{u.companies.join(', ') || '—'}</div>,
    actions: (
      <div className="flex justify-end gap-2 pr-2 min-w-[10rem]">
        <button
          className="btn-secondary px-2 py-1"
          onClick={() => copyEmails(filtered.filter(c => (c.emails || []).map(e=>e.toLowerCase()).includes(u.email.toLowerCase())))}
        >
          <Copy className="w-4 h-4" /> Copy
        </button>
        <a className="btn px-2 py-1" href={`mailto:${encodeURIComponent(u.email)}`}>
          <Mail className="w-4 h-4" /> Mailto
        </a>
      </div>
    ),
  }))

  return (
    <AppShell title="Contacts">
      {/* Toolbar, Filters, Saved Views */}
      <div className="card mb-3">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button className={filters.tab === 'contacts' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, tab: 'contacts' }))}>
              Contacts
            </button>
            <button className={filters.tab === 'users' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, tab: 'users' }))}>
              Contact Users
            </button>
            <button className={filters.tab === 'deleted' ? 'btn' : 'btn-secondary'} onClick={() => setFilters(f => ({ ...f, tab: 'deleted' }))}>
              Deleted
            </button>
          </div>

          <div className="flex items-center gap-2">
            <SavedViews<Filters>
              storageKey={VIEWS_KEY}
              currentFilters={filters}
              onLoad={(next) => setFilters(next)}
            />
            <div className="popover relative">
              <details>
                <summary className="btn-secondary px-3 py-1.5">
                  Density: {filters.density === 'compact' ? 'Compact' : 'Comfort'}
                </summary>
                <div className="popover p-2 mt-2 w-48">
                  <button className="btn-secondary w-full mb-2" onClick={() => setFilters(f => ({ ...f, density: 'comfort' }))}>Comfort</button>
                  <button className="btn-secondary w-full"     onClick={() => setFilters(f => ({ ...f, density: 'compact' }))}>Compact</button>
                </div>
              </details>
            </div>
          </div>
        </div>

        <div className="card-body grid lg:grid-cols-3 gap-3 items-start">
          {/* Search + state + hasEmail */}
          <div className="space-y-3">
            <div>
              <label className="text-sm">Search</label>
              <input className="input" placeholder="Search contacts…" value={qRaw} onChange={e => setQRaw(e.target.value)} />
              <div className="text-xs muted mt-1">
                Showing <span className="tabular-nums">{filtered.length.toLocaleString()}</span> contact{filtered.length === 1 ? '' : 's'}.
              </div>
            </div>

            <div>
              <label className="text-sm">State</label>
              <input className="input" placeholder="e.g. GA" value={filters.state} onChange={(e) => setFilters(f => ({ ...f, state: e.target.value }))} />
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={filters.hasEmail} onChange={(e) => setFilters(f => ({ ...f, hasEmail: e.target.checked }))} />
              Has Email
            </label>

            <div className="pt-1">
              <button className="btn w-full flex items-center justify-center gap-2" onClick={() => copyEmails()} title="Copy unique emails of the filtered list">
                <Copy className="w-4 h-4" />
                Copy Emails (filtered)
              </button>
            </div>
          </div>

          {/* Types + Tags */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Types</label>
              <MultiSelect
                options={typeOptions}
                value={filters.types}
                onChange={(vals) => setFilters(f => ({ ...f, types: vals }))}
                placeholder="All types"
                storageKey={TYPES_KEY}
              />
            </div>
            <div>
              <label className="text-sm">Tags</label>
              <MultiSelect
                options={tagOptions}
                value={filters.tags}
                onChange={(vals) => setFilters(f => ({ ...f, tags: vals }))}
                placeholder="All tags"
                storageKey={TAGS_KEY}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tables */}
      {loading ? (
        <Skeleton className="h-64" />
      ) : filters.tab === 'users' ? (
        userRows.length === 0 ? (
          <EmptyState title="No contact users">
            Adjust filters or add emails to contacts.
          </EmptyState>
        ) : (
          <DataTable columns={userCols} rows={userRows} filename="contact-users.csv" storageKey="tbl:contact-users:v1" />
        )
      ) : rows.length === 0 ? (
        <EmptyState title={filters.tab === 'deleted' ? 'Trash is empty' : 'No contacts found'}>
          {filters.tab === 'deleted' ? 'Restored contacts show here until permanently cleared.' : 'Try adjusting filters or create a new contact.'}
          <div className="mt-3">
            <button className="btn" onClick={() => openEdit()}>Create Contact</button>
          </div>
        </EmptyState>
      ) : (
        <div className={densityClass}>
          <DataTable columns={cols} rows={rows} filename="contacts.csv" storageKey="tbl:contacts:v1" />
        </div>
      )}

      {/* Edit Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing?.fullName || 'Contact'}>
        {editing && (
          <div className="drawer-panel space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm">Full Name</label>
                <input className="input" value={editing.fullName} onChange={e => setEditing({ ...editing, fullName: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Company</label>
                <input className="input" value={editing.company || ''} onChange={e => setEditing({ ...editing, company: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Emails (comma-separated)</label>
                <input className="input" value={(editing.emails || []).join(', ')} onChange={e => setEditing({ ...editing, emails: parseEmails(e.target.value) })} />
              </div>
              <div>
                <label className="text-sm">Phone</label>
                <input className="input" value={editing.phone || ''} onChange={e => setEditing({ ...editing, phone: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Address 1</label>
                <input className="input" value={editing.address1 || ''} onChange={e => setEditing({ ...editing, address1: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Address 2</label>
                <input className="input" value={editing.address2 || ''} onChange={e => setEditing({ ...editing, address2: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">City</label>
                <input className="input" value={editing.city || ''} onChange={e => setEditing({ ...editing, city: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">State</label>
                <input className="input" value={editing.state || ''} onChange={e => setEditing({ ...editing, state: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">ZIP</label>
                <input className="input" value={editing.zip || ''} onChange={e => setEditing({ ...editing, zip: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Types</label>
                <MultiSelect
                  options={typeOptions}
                  value={editing.types || []}
                  onChange={(vals) => setEditing({ ...editing, types: vals })}
                  placeholder="Select types"
                  storageKey={TYPES_KEY}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Tags</label>
                <MultiSelect
                  options={tagOptions}
                  value={editing.tags || []}
                  onChange={(vals) => setEditing({ ...editing, tags: vals })}
                  placeholder="Select tags"
                  storageKey={TAGS_KEY}
                />
              </div>
            </div>

            <div className="flex gap-2">
              {!editing.deleted ? (
                <button className="btn btn-danger" onClick={() => softDelete(editing)}>
                  <Trash2 className="w-4 h-4" /> <span className="ml-1">Delete</span>
                </button>
              ) : (
                <button className="btn btn-secondary" onClick={() => restore(editing)}>
                  <Undo2 className="w-4 h-4" /> <span className="ml-1">Restore</span>
                </button>
              )}
              <div className="flex-1" />
              <button className="btn btn-secondary" onClick={() => setDrawerOpen(false)}>Cancel</button>
              <button className="btn" onClick={saveEditing}>
                <Save className="w-4 h-4" /> <span className="ml-1">Save</span>
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </AppShell>
  )
}

/* ----------------- helpers ----------------- */

function upsertById(list: Contact[], item: Contact) {
  const idx = list.findIndex(x => x.id === item.id)
  if (idx === -1) return [item, ...list]
  const next = [...list]; next[idx] = item; return next
}
function parseEmails(value: string | string[] | undefined): string[] {
  if (!value) return []
  if (Array.isArray(value)) return value.map((e) => e.trim()).filter(Boolean)
  return value.split(',').map((e) => e.trim()).filter(Boolean)
}
function normalizeContacts(list: Contact[]): Contact[] {
  return (list || []).map(c => ({
    id: c.id || crypto.randomUUID(),
    fullName: c.fullName || '',
    company: c.company || '',
    emails: Array.isArray(c.emails) ? c.emails.filter(Boolean) : parseEmails((c as any).email || ''),
    phone: c.phone || '',
    address1: c.address1 || '', address2: c.address2 || '',
    city: c.city || '', state: c.state || '', zip: c.zip || '',
    types: Array.isArray(c.types) ? c.types : [],
    tags: Array.isArray(c.tags) ? c.tags : [],
    deleted: !!c.deleted,
    createdAt: c.createdAt, updatedAt: c.updatedAt,
  }))
}
